// Зөвлөгөө (сурагч): миний уулзалтууд — үзүүлэлт, удахгүй болох уулзалт, зөвлөхөөс өгсөн ажил, өнгөрсөн уулзалт + үнэлгээ.
// Зөвхөн сурагч (API бусдад 400 "Зөвхөн сурагч"). Уулзалт захиалах → /counseling/book, дэлгэрэнгүй → /counseling/:id
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Spin, message } from "antd";
import { FiCalendar, FiCheck, FiCheckSquare, FiChevronRight, FiClock, FiInbox, FiList, FiPlus, FiStar, FiVolume2, FiX } from "react-icons/fi";
import ActionItem from "./ActionItem";
import {
  api, Avatar, Badge, Empty, Stars, TypePill, dayKey, dayLabel, dueSoon, fullName, goldBtn, hm, md, timeRange, todayKey, addDaysKey,
} from "./common";

function Kpi({ label, value, unit, sub, icon: Icon, bg, fg }) {
  return (
    <div className="tds-card p-4 md:p-5 flex flex-col gap-2 md:gap-3 min-w-0">
      <div className="flex items-start justify-between gap-2">
        <span className="text-[13px] md:text-sm font-semibold text-[#6E5E8A] leading-snug">{label}</span>
        <span className="h-9 w-9 md:h-10 md:w-10 rounded-[10px] inline-flex items-center justify-center shrink-0" style={{ background: bg, color: fg }}>
          <Icon size={19} />
        </span>
      </div>
      <span className="text-[30px] md:text-[36px] font-extrabold leading-none text-[#5C1090]">
        {value}
        {unit && <span className="text-base md:text-lg font-bold text-[#6E5E8A]"> {unit}</span>}
      </span>
      <span className="text-xs md:text-[13px] text-[#6E5E8A] leading-snug">{sub}</span>
    </div>
  );
}

function UpcomingRow({ m }) {
  return (
    <Link
      to={`/counseling/${m.id}`}
      className="flex flex-wrap sm:flex-nowrap items-center gap-x-4 gap-y-2.5 p-3.5 rounded-xl border border-[#E5D8F5] bg-white text-gray-900 hover:text-gray-900 hover:bg-[#FAF7FF] transition-colors"
    >
      <span className="w-[104px] flex flex-col gap-0.5 shrink-0">
        <span className="text-xs font-bold tracking-[0.05em] uppercase text-[#6E5E8A]">{dayLabel(m.startTime)}</span>
        <span className="text-sm font-bold tabular-nums">{timeRange(m.startTime, m.durationMin)}</span>
      </span>
      <span className="flex items-center gap-3 flex-[1_1_200px] sm:flex-1 min-w-0">
        <Avatar person={m.counselor} size={32} dark />
        <span className="flex flex-col min-w-0">
          <span className="text-[15px] font-bold leading-snug truncate">{m.topic || "Зөвлөгөө"}</span>
          <span className="text-[13px] text-[#6E5E8A] truncate">{fullName(m.counselor)}{m.counselor?.title ? ` · ${m.counselor.title}` : ""}</span>
        </span>
      </span>
      <span className="flex items-center gap-2 flex-wrap sm:flex-nowrap ml-auto shrink-0">
        <TypePill m={m} />
        {m.stage !== "upcoming" ? <Badge stage={m.stage} /> : dayKey(m.startTime) === todayKey() ? <Badge stage="live">Өнөөдөр</Badge> : null}
        <FiChevronRight className="text-[#5C1090] shrink-0" size={18} />
      </span>
    </Link>
  );
}

function PastRow({ m }) {
  const rate = m.rating ? <Stars n={m.rating.stars} /> : <span className="text-xs font-bold text-[#5C1090] inline-flex items-center gap-1"><FiStar size={12} /> Үнэлэх</span>;
  return (
    <li>
      <Link
        to={`/counseling/${m.id}`}
        className="flex items-center gap-x-4 px-3.5 py-3 rounded-xl text-gray-900 hover:text-gray-900 hover:bg-[#FAF7FF] transition-colors"
      >
        <span className="w-[44px] sm:w-[52px] text-sm font-bold tabular-nums shrink-0 self-start sm:self-center pt-0.5 sm:pt-0">{md(m.startTime)}</span>
        <span className="flex-1 min-w-0 flex flex-col sm:flex-row sm:items-center gap-x-4 gap-y-0.5">
          <span className="sm:w-[150px] shrink-0 inline-flex items-center gap-2 text-[13px] sm:text-sm text-[#6E5E8A] sm:text-gray-900 min-w-0">
            <span className="hidden sm:inline-flex"><Avatar person={m.counselor} size={28} dark /></span>
            <span className="truncate">{fullName(m.counselor)}</span>
          </span>
          <span className="text-[15px] font-semibold truncate min-w-0 flex-1">{m.topic || "Зөвлөгөө"}</span>
          <span className="sm:hidden mt-1">{rate}</span>
        </span>
        <span className="hidden md:block w-[90px] text-[13px] text-[#6E5E8A] shrink-0">{m.type === "online" ? "Онлайн" : m.type === "in_person" ? "Биечлэн" : ""}</span>
        <span className="hidden sm:flex w-[92px] shrink-0">{rate}</span>
        <FiChevronRight className="text-[#5C1090] shrink-0" size={18} />
      </Link>
    </li>
  );
}

// Шар мэдэгдэл — хамгийн чухал нэг зүйл: явагдаж буй > өнөөдөр > үнэлээгүй > хүлээгдэж буй хүсэлт
function bannerOf(data) {
  const up = data.upcoming || [];
  const live = up.find((m) => m.stage === "live");
  if (live) return { id: `live-${live.id}`, text: <><strong>Уулзалт явагдаж байна:</strong> {live.topic || "Зөвлөгөө"} · {fullName(live.counselor)}</>, to: `/counseling/${live.id}`, cta: "Нэгдэх" };
  const today = up.find((m) => m.stage === "upcoming" && dayKey(m.startTime) === todayKey());
  if (today) return { id: `today-${today.id}`, text: <><strong>Өнөөдөр {hm(today.startTime)}-д</strong> {fullName(today.counselor)}-тэй уулзалттай: {today.topic || "Зөвлөгөө"}</>, to: `/counseling/${today.id}`, cta: "Дэлгэрэнгүй" };
  const unrated = (data.past || []).find((m) => !m.rating);
  if (unrated) return { id: `rate-${unrated.id}`, text: <><strong>Үнэлгээ өгөөрэй:</strong> {md(unrated.startTime)}-ны “{unrated.topic || "Зөвлөгөө"}” уулзалт ямар байсан бэ?</>, to: `/counseling/${unrated.id}`, cta: "Үнэлэх" };
  const req = up.find((m) => m.stage === "requested");
  if (req) return { id: `req-${req.id}`, text: <><strong>Хүсэлт илгээгдсэн:</strong> {dayLabel(req.startTime)} {hm(req.startTime)} — зөвлөх баталгаажуулмагц мэдэгдэл ирнэ.</>, to: `/counseling/${req.id}`, cta: "Харах" };
  return null;
}

export default function Counseling() {
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);
  const [mode, setMode] = useState("week");
  const [showAll, setShowAll] = useState(false);
  const [busy, setBusy] = useState(null);
  const [hidden, setHidden] = useState(null);

  const load = useCallback(() => {
    api("get", "/home").then((d) => { setData(d); setFailed(false); }).catch(() => setFailed(true));
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (a, done) => {
    setBusy(a.id);
    const doneAt = done ? new Date().toISOString() : null;
    setData((d) => ({ ...d, actions: d.actions.map((x) => (x.id === a.id ? { ...x, doneAt } : x)), kpi: { ...d.kpi, tasks: d.kpi.tasks + (done ? -1 : 1) } }));
    try {
      await api("put", `/action/${a.id}`, { done });
      if (done) message.success("Ажил дууссан гэж тэмдэглэлээ");
    } catch (e) {
      load();
    } finally {
      setBusy(null);
    }
  };

  const view = useMemo(() => {
    if (!data) return null;
    const up = data.upcoming || [];
    const t = todayKey();
    const wk = addDaysKey(7);
    const today = up.filter((m) => dayKey(m.startTime) === t || m.stage === "live");
    const week = up.filter((m) => dayKey(m.startTime) <= wk || m.stage === "live");
    const list = showAll ? up : mode === "today" ? today : week;
    const later = up.length - list.length;
    const open = (data.actions || []).filter((a) => !a.doneAt);
    const next = up.find((m) => m.stage !== "requested") || up[0];
    const counselorCount = new Set((data.past || []).map((m) => m.counselor?.id)).size;
    return { list, later, open, next, counselorCount, soon: open.filter((a) => dueSoon(a.dueDate)).length };
  }, [data, mode, showAll]);

  if (failed && !data) {
    return (
      <div className="pt-6 pb-6 max-w-3xl mx-auto">
        <div className="tds-card p-6"><Empty icon={FiInbox} title="Мэдээлэл ачаалж чадсангүй" text="Зөвлөгөөний хэсэг зөвхөн сурагчид нээлттэй. Дахин оролдоно уу." /></div>
      </div>
    );
  }
  if (!data) return <div className="flex justify-center py-16"><Spin /></div>;

  const { kpi } = data;
  const banner = bannerOf(data);

  return (
    <div className="pt-4 md:pt-6 pb-8 flex flex-col gap-5 md:gap-7">
      {banner && hidden !== banner.id && (
        <div role="status" className="bg-[#F2CD30] text-[#1c1c1c] rounded-xl pl-4 pr-2 py-2.5 flex items-center gap-3">
          <FiVolume2 size={19} className="shrink-0" />
          <p className="m-0 flex-1 text-sm leading-snug min-w-0">{banner.text}</p>
          <Link to={banner.to} className="text-sm font-bold text-[#1c1c1c] hover:text-[#1c1c1c] underline underline-offset-4 shrink-0">{banner.cta}</Link>
          <button type="button" onClick={() => setHidden(banner.id)} aria-label="Мэдэгдлийг хаах" className="h-8 w-8 rounded-lg inline-flex items-center justify-center hover:bg-black/10 shrink-0">
            <FiX size={16} />
          </button>
        </div>
      )}

      <section className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex flex-col gap-2 min-w-0">
          <h1 className="tds-h1">Миний зөвлөгөө.</h1>
          <p className="m-0 text-[15px] md:text-base text-[#6E5E8A]">Зөвлөхүүдтэйгээ уулзаж, өгсөн ажлаа эндээс хянана.</p>
        </div>
        <Link to="/counseling/book" className={`${goldBtn} w-full sm:w-auto`}>
          <FiPlus size={18} /> Уулзалт захиалах
        </Link>
      </section>

      <section aria-label="Миний зөвлөгөөний үзүүлэлт" className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
        <Kpi label="Орсон уулзалт" value={kpi.attended} icon={FiCheck} bg="#5C1090" fg="#fff"
          sub={kpi.attended ? `${view.counselorCount} зөвлөхтэй уулзсан` : "Одоогоор уулзаагүй"} />
        <Kpi label="Удахгүй болох" value={kpi.upcoming} icon={FiCalendar} bg="#00BCD4" fg="#1c1c1c"
          sub={view.next ? `Дараагийнх: ${dayLabel(view.next.startTime)} ${hm(view.next.startTime)}` : "Товлосон уулзалт алга"} />
        <Kpi label="Миний хийх ажил" value={kpi.tasks} icon={FiList} bg="#F2CD30" fg="#1c1c1c"
          sub={kpi.tasks ? `${view.soon} нь 7 хоногт дуусна` : "Үлдсэн ажил алга"} />
        <Kpi label="Зөвлөгөөний цаг" value={kpi.hours} unit="цаг" icon={FiClock} bg="#0E7490" fg="#fff" sub="Нийт уулзалтын цаг" />
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 md:gap-5 items-stretch">
        <section aria-labelledby="up-h" className="lg:col-span-3 tds-card px-4 py-4 md:px-6 md:py-5 flex flex-col gap-4 min-w-0">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 id="up-h" className="tds-h2">Удахгүй болох уулзалт</h2>
            <div role="radiogroup" aria-label="Хугацаа" className="inline-flex p-1 rounded-[10px] bg-[#F3EEFF] gap-1">
              {[["today", "Өнөөдөр"], ["week", "Энэ 7 хоног"]].map(([v, l]) => (
                <button key={v} type="button" role="radio" aria-checked={mode === v && !showAll}
                  onClick={() => { setMode(v); setShowAll(false); }}
                  className={`h-8 px-3 rounded-lg text-[13px] font-bold transition-colors ${mode === v && !showAll ? "bg-white text-[#5C1090] shadow-sm" : "text-[#6E5E8A] hover:text-[#5C1090]"}`}>
                  {l}
                </button>
              ))}
            </div>
          </div>
          {view.list.length ? (
            <div className="flex flex-col gap-2.5">
              {view.list.map((m) => <UpcomingRow key={m.id} m={m} />)}
            </div>
          ) : (data.upcoming || []).length ? (
            <Empty icon={FiCalendar} title={mode === "today" ? "Өнөөдөр уулзалт алга" : "Энэ 7 хоногт уулзалт алга"} />
          ) : (
            <Empty icon={FiCalendar} title="Товлосон уулзалт алга"
              text="Их сургууль, эссэ, хичээл, сэтгэл санаа — ямар ч сэдвээр зөвлөхтэйгөө уулзаж болно.">
              <Link to="/counseling/book" className="mt-2 text-sm font-bold text-[#5C1090] inline-flex items-center gap-1">
                Уулзалт захиалах <FiChevronRight size={16} />
              </Link>
            </Empty>
          )}
          {view.later > 0 && (
            <button type="button" onClick={() => setShowAll(true)} className="self-start text-sm font-bold text-[#5C1090] inline-flex items-center gap-1 hover:underline">
              Дараа нь {view.later} уулзалт — бүгдийг харах <FiChevronRight size={15} />
            </button>
          )}
        </section>

        <section aria-labelledby="todo-h" className="lg:col-span-2 tds-card px-4 py-4 md:px-6 md:py-5 flex flex-col gap-3.5 min-w-0">
          <div className="flex items-baseline justify-between gap-3">
            <h2 id="todo-h" className="tds-h2">Зөвлөхөөс өгсөн ажил</h2>
            {data.actions.length > 0 && <span className="text-[13px] text-[#6E5E8A] shrink-0">{view.open.length} үлдсэн</span>}
          </div>
          {data.actions.length ? (
            <ul aria-label="Хийх ажлын жагсаалт" className="list-none m-0 p-0 max-h-[440px] overflow-y-auto border border-[#E5D8F5] rounded-xl bg-white">
              {data.actions.map((a) => (
                <ActionItem key={a.id} a={a} busy={busy === a.id} onToggle={toggle}
                  from={[fullName(a.counselor), a.meetingTopic].filter(Boolean).join(" · ")} />
              ))}
            </ul>
          ) : (
            <Empty icon={FiCheckSquare} title="Хийх ажил алга" text="Уулзалтын дараа зөвлөхийн өгсөн даалгавар энд гарч, дууссаныг нь тэмдэглэнэ." />
          )}
        </section>
      </div>

      <section aria-labelledby="past-h" className="flex flex-col gap-3">
        <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
          <h2 id="past-h" className="tds-h2">Өнгөрсөн уулзалтууд</h2>
          <span className="text-[13px] text-[#6E5E8A]">Зөвлөхийн тэмдэглэл, өгсөн ажил, миний үнэлгээ</span>
        </div>
        <div className="tds-card p-1.5">
          {data.past.length ? (
            <ul className="list-none m-0 p-0">{data.past.map((m) => <PastRow key={m.id} m={m} />)}</ul>
          ) : (
            <div className="px-4 py-6 text-sm text-[#6E5E8A] text-center">Өнгөрсөн уулзалт алга. Уулзалт дууссаны дараа зөвлөхийн тэмдэглэл энд хадгалагдана.</div>
          )}
        </div>
      </section>
    </div>
  );
}
